<script setup lang="ts">
import { RouterView, RouterLink } from 'vue-router'
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getTheme, getShowHeader, getThemeColorOverrides, isOrderCreationAllowed, loadServerConfig } from '@/api/config'
import { initParentScrollTracking } from '@/utils/iframe'

const route = useRoute()
const showHeader = ref(true) // Default to showing header
const allowOrderCreation = ref(true)
const appContainer = ref<HTMLElement | null>(null)

// ResizeObserver for iframe height reporting
let resizeObserver: ResizeObserver | null = null
let heightInterval: number | null = null
let heightDebounceTimer: number | null = null
let lastReportedHeight = 0
let heightReportingStarted = false

const customThemeProperties = [
  '--button-background-color',
  '--button-background',
  '--button-background-hover',
  '--button-text-color',
  '--accent-fill-text-color',
  '--accent-line-color',
  '--accent-primary',
  '--accent-hover',
  '--accent-subtle',
  '--shadow-glow',
  '--page-background-color',
]

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  const value = parseInt(normalized, 16)
  const red = (value >> 16) & 255
  const green = (value >> 8) & 255
  const blue = value & 255
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function clearThemeColorOverrides() {
  for (const property of customThemeProperties) {
    document.documentElement.style.removeProperty(property)
  }
}

function applyThemeColorOverrides() {
  clearThemeColorOverrides()

  const colors = getThemeColorOverrides()
  const rootStyle = document.documentElement.style

  if (colors.buttonBackgroundColor) {
    const subtle = hexToRgba(colors.buttonBackgroundColor, 0.12)
    const glow = hexToRgba(colors.buttonBackgroundColor, 0.25)
    rootStyle.setProperty('--button-background-color', colors.buttonBackgroundColor)
    rootStyle.setProperty('--button-background', colors.buttonBackgroundColor)
    rootStyle.setProperty('--button-background-hover', colors.buttonBackgroundColor)
    rootStyle.setProperty('--accent-primary', colors.buttonBackgroundColor)
    rootStyle.setProperty('--accent-hover', colors.buttonBackgroundColor)
    rootStyle.setProperty('--accent-subtle', subtle)
    rootStyle.setProperty('--shadow-glow', `0 0 20px ${glow}`)
  }

  if (colors.buttonTextColor) {
    rootStyle.setProperty('--button-text-color', colors.buttonTextColor)
    rootStyle.setProperty('--accent-fill-text-color', colors.buttonTextColor)
  }

  if (colors.accentLineColor) {
    rootStyle.setProperty('--accent-line-color', colors.accentLineColor)
  }

  if (colors.pageBackgroundColor) {
    rootStyle.setProperty('--page-background-color', colors.pageBackgroundColor)
  }
}

/**
 * Send the current document height to the parent window.
 * This allows the parent page to resize the iframe to fit the content,
 * eliminating the need for scrollbars inside the iframe.
 */
function sendHeightToParent() {
  if (window.parent === window) return // Not in an iframe

  // Don't send height updates while in fullscreen mode (e.g., detail modal).
  // Fullscreen sets the iframe to 100vh; a height message would override that.
  if (document.documentElement.classList.contains('iframe-fullscreen')) return

  // Measure the content container element rather than document.documentElement.scrollHeight.
  // scrollHeight of documentElement can never be less than the iframe viewport height,
  // which prevents the iframe from collapsing when content shrinks.
  const el = appContainer.value
  const height = el
    ? el.scrollHeight
    : Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      )

  // Only send if height actually changed
  if (height > 0 && height !== lastReportedHeight) {
    lastReportedHeight = height
    window.parent.postMessage(
      { type: 'FB_IFRAME_HEIGHT', height: height },
      '*'
    )
  }
}

/**
 * Debounced version of sendHeightToParent to prevent rapid-fire updates
 * that can interfere with image loading
 */
function sendHeightToParentDebounced() {
  if (heightDebounceTimer) {
    clearTimeout(heightDebounceTimer)
  }
  heightDebounceTimer = window.setTimeout(() => {
    sendHeightToParent()
    heightDebounceTimer = null
  }, 100) // 100ms debounce
}

/**
 * Start monitoring height changes and reporting to parent
 */
function startHeightReporting() {
  if (heightReportingStarted) return
  heightReportingStarted = true

  // Send initial height immediately
  sendHeightToParent()

  // Use ResizeObserver on the content container if available
  const target = appContainer.value
  if ('ResizeObserver' in window && target) {
    resizeObserver = new ResizeObserver(sendHeightToParentDebounced)
    resizeObserver.observe(target)
  } else {
    // Fallback: poll for height changes
    heightInterval = window.setInterval(sendHeightToParent, 500)
  }

  // Also send on load and resize events (debounced)
  window.addEventListener('load', sendHeightToParent)
  window.addEventListener('resize', sendHeightToParentDebounced)

  // Start listening for parent scroll position updates (for modal positioning)
  initParentScrollTracking()
}

/**
 * Stop height reporting
 */
function stopHeightReporting() {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (heightInterval) {
    clearInterval(heightInterval)
    heightInterval = null
  }
  if (heightDebounceTimer) {
    clearTimeout(heightDebounceTimer)
    heightDebounceTimer = null
  }
  window.removeEventListener('load', sendHeightToParent)
  window.removeEventListener('resize', sendHeightToParentDebounced)
}

/**
 * Apply theme and header visibility from config
 */
function applyConfig() {
  const theme = getTheme()
  document.documentElement.setAttribute('data-theme', theme)
  applyThemeColorOverrides()
  showHeader.value = getShowHeader()
  allowOrderCreation.value = isOrderCreationAllowed()

  // Always enable embed-mode when in an iframe, regardless of showHeader setting.
  // This ensures modal positioning CSS overrides and height reporting work correctly.
  const inIframe = window.parent !== window

  if (inIframe || !showHeader.value) {
    document.documentElement.classList.add('embed-mode')
    document.body.classList.add('embed-mode')
    startHeightReporting()
  } else {
    document.documentElement.classList.remove('embed-mode')
    document.body.classList.remove('embed-mode')
    stopHeightReporting()
  }

  console.log(`Applied config: theme=${theme}, showHeader=${showHeader.value}, inIframe=${inIframe}`)
}

// Apply config after server config is loaded
onMounted(async () => {
  await loadServerConfig()
  applyConfig()
})

// Cleanup on unmount
onUnmounted(() => {
  stopHeightReporting()
})

// Re-apply config when route changes (in case tenant changes)
watch(() => route.query.t, async () => {
  // Config will be reloaded by router beforeEach
  // Give it a moment to load
  setTimeout(applyConfig, 100)
})
</script>

<template>
  <div ref="appContainer" class="app-container" :class="{ 'no-header': !showHeader }">
    <!-- Navigation Header (hidden for iframe embeds by default) -->
    <header v-if="showHeader" class="app-header">
      <div class="header-content">
        <RouterLink to="/" class="logo">
          <span class="logo-icon">◈</span>
          <span class="logo-text">ClassifiedScout</span>
        </RouterLink>
        
        <nav class="main-nav">
          <RouterLink to="/" class="nav-link">Home</RouterLink>
          <RouterLink v-if="allowOrderCreation" to="/create-ad" class="nav-link">Create Ad</RouterLink>
          <RouterLink to="/setup" class="nav-link settings-link" title="API Settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </RouterLink>
        </nav>
      </div>
    </header>

    <!-- Main Content -->
    <main class="app-main">
      <RouterView />
    </main>

    <!-- Footer (also hidden when header is hidden) -->
    <footer v-if="showHeader" class="app-footer">
      <p>&copy; {{ new Date().getFullYear() }} AccountScout - Classified Order Entry</p>
    </footer>
  </div>
</template>

<style scoped>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--page-background-color, var(--bg-primary));
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  backdrop-filter: blur(12px);
}

.header-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--spacing-6);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.25rem;
}

.logo-icon {
  font-size: 1.5rem;
  color: var(--accent-primary);
}

.logo-text {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.main-nav {
  display: flex;
  gap: var(--spacing-1);
}

.nav-link {
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--radius-md);
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  transition: all var(--transition-fast);
}

.nav-link:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.nav-link.router-link-active {
  color: var(--accent-primary);
  background: var(--accent-subtle);
}

.settings-link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2);
  margin-left: var(--spacing-2);
  border-left: 1px solid var(--border-color);
  padding-left: var(--spacing-4);
}

.settings-link svg {
  opacity: 0.7;
  transition: opacity var(--transition-fast);
}

.settings-link:hover svg {
  opacity: 1;
}

.app-main {
  flex: 1;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: var(--spacing-8) var(--spacing-6);
}

.app-footer {
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-6);
  text-align: center;
  color: var(--text-tertiary);
  font-size: 0.875rem;
}

/* When header is hidden (iframe embed mode) */
.app-container.no-header .app-main {
  padding-top: var(--spacing-4);
}

/* Embed mode - fill available space and allow scrolling */
.app-container.no-header {
  min-height: 100%;
  height: auto;
}
</style>
